import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowRight, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useVerifiedArtistPrivileges } from "@/hooks/useVerifiedArtistPrivileges";
import { useAnnouncementBannerVisible } from "@/components/layout/AnnouncementBanner";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/useResponsive";

const DISMISS_KEY = "verification-upgrade-banner-dismissed";

export function useVerificationBannerVisible() {
  const { user, userRole } = useAuth();
  const { isVerified, isLoading } = useVerifiedArtistPrivileges(user?.id);
  const location = useLocation();

  const isOnVerificationPage =
    location.pathname === "/verification" ||
    (location.pathname === "/dashboard/artist" &&
      new URLSearchParams(location.search).get("tab") === "verification");

  const dismissed =
    typeof window !== "undefined" &&
    sessionStorage.getItem(DISMISS_KEY) === "true";

  if (
    !user ||
    userRole !== "artist" ||
    isLoading ||
    isVerified ||
    isOnVerificationPage ||
    dismissed
  ) {
    return false;
  }
  return true;
}

export function VerificationUpgradeBanner() {
  const visible = useVerificationBannerVisible();
  const announcementVisible = useAnnouncementBannerVisible();
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const { data: displayName } = useQuery({
    queryKey: ["artist-display-name", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("public_profiles")
        .select("display_name")
        .eq("id", user.id)
        .single();
      return data?.display_name || null;
    },
    enabled: !!user?.id && visible,
    staleTime: 5 * 60 * 1000,
  });

  if (!visible || dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
  };

  const firstName = displayName?.split("")[0] || "Artist";

  // Mobile/Tablet: Bottom-sheet modal
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 z-[70] bg-black/40 animate-fade-in"
          onClick={handleDismiss}
        />

        {/* Bottom sheet */}
        <div className="fixed bottom-0 left-0 right-0 z-[70] animate-[slide-up_0.35s_ease-out_forwards]">
          <div className="bg-card text-card-foreground rounded-t-2xl px-6 pt-6 pb-10">
            {/* Dismiss */}
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-muted transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>

            {/* Heading */}
            <h3 className="text-lg font-bold text-foreground mb-4">
              Hey {firstName}, go Verified!
            </h3>

            {/* Benefits */}
            <ul className="space-y-3 mb-5">
              {[
                "Upload up to 50 tracks",
                "Get your verified badge",
                "Enhanced visibility",
              ].map((benefit) => (
                <li
                  key={benefit}
                  className="flex items-center gap-2.5 text-sm text-muted-foreground"
                >
                  <CheckCircle2 className="w-4 h-4 text-primary-light shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>

            {/* Price */}
            <p className="text-xl font-bold text-foreground mb-1">
              ₦25,800
              <span className="text-sm font-normal text-muted-foreground">
                /yr
              </span>
            </p>
            <p className="text-xs text-muted-foreground mb-5">Incl. 7.5% VAT</p>

            {/* CTA */}
            <Button
              className="w-full h-11 font-bold"
              onClick={() => {
                handleDismiss();
                navigate("/verification");
              }}
            >
              Get Verified
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </>
    );
  }

  // Desktop: Floating pill banner
  return (
    <div
      className={`fixed left-1/2 -translate-x-1/2 z-40 w-auto max-w-[95%] bg-primary text-primary-foreground rounded-full ${announcementVisible ? "top-[104px]" : "top-16"}`}
    >
      <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-2.5">
        <div className="flex items-center gap-3 min-w-0">
          <p className="text-sm font-medium truncate">
            <span className="font-bold">Hey {firstName}, go Verified!</span>
            {" "}
            <span className="hidden sm:inline">
              Upload 50 tracks, get your badge & enhanced visibility.{" "}
            </span>
            <span className="font-bold">₦25,800/yr</span>{" "}
            <span className="text-xs opacity-80">(incl. VAT)</span>
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="secondary"
            className="bg-white text-primary hover:bg-white/90 font-bold text-xs h-8 px-4 transition-transform duration-200 hover:scale-105 rounded-full"
            onClick={() => navigate("/verification")}
          >
            Get Verified
            <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
          <button
            onClick={handleDismiss}
            className="p-1 rounded-full hover:bg-white/20 transition-colors"
            aria-label="Dismiss banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
